/*
 * Copyright 2025 Intuition Robotics ltd
 */
/*
 * Thunderstorm is a full web app framework!
 *
 * Typescript & Express backend infrastructure that natively runs on firebase function
 * Typescript & React frontend infrastructure
 *
 * Copyright (C) 2020 Adam van der Kruk aka TacB0sS
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {ObjectTS} from "@intuitionrobotics/ts-common";
import {
	CSSProperties
} from "react";
import * as React from "react";
import {Stylable} from "@intuitionrobotics/thunderstorm/frontend";

export type HeaderRenderer<T extends ObjectTS> = {
	[P in keyof T]: (columnKey: P) => React.ReactNode
};

export type ActionItemRenderer<P> = (rowIndex: number, actionKey: P) => React.ReactNode;
export type ActionsRenderer<A extends ObjectTS> = {
	[P in keyof A]: ActionItemRenderer<P>
};

export type CellRenderer<P, V> = (cellValue: V, rowIndex: number, columnKey: P) => React.ReactNode;
export type RowRenderer<T extends ObjectTS> = {
	[P in keyof T]: CellRenderer<P, T[P]>
};

export type Styler<P> = Stylable | ((p: P) => Stylable)

export type TableProps<T extends ObjectTS, A extends ObjectTS = never> = Stylable & {
	id: string,
	header: (keyof T)[],
	rows: T[],
	headerRenderer: ((columnKey: keyof T) => React.ReactNode) | HeaderRenderer<T>,
	cellRenderer: CellRenderer<keyof T, T[keyof T]> | RowRenderer<T>
	actions?: (keyof A)[],
	actionsRenderer?: ActionsRenderer<A> | ActionItemRenderer<keyof A>
	head?: Stylable
	body?: Stylable
	trHead?: Stylable
	tr?: (cellValue: T, rowIndex: number) => Stylable
	th?: Styler<keyof T>
	td?: Styler<keyof T>
	tdActions?: Styler<keyof A>
};

export class TS_Table<T extends ObjectTS, A extends ObjectTS = never>
	extends React.Component<TableProps<T, A>, any> {
	constructor(p: TableProps<T, A>) {
		super(p);
	}

	render() {
		return <table className={this.props.className} style={this.props.style}>
			<thead className={this.props.head?.className} style={this.props.head?.style}>
			{this.renderTableHeader()}
			</thead>
			<tbody className={this.props.body?.className} style={this.props.body?.style}>
			{this.renderTableBody()}
			</tbody>
		</table>;
	}

	private renderTableHeader() {
		let renderers: HeaderRenderer<T>;
		if (typeof this.props.headerRenderer === "object")
			renderers = this.props.headerRenderer;
		else
			renderers = this.props.header.reduce((toRet, headerProp) => {
				toRet[headerProp] = this.props.headerRenderer as ((columnKey: keyof T) => React.ReactNode);
				return toRet;
			}, {} as HeaderRenderer<T>);

		return (
			<tr key={`${this.props.id}-0`} className={this.props.trHead?.className} style={this.props.trHead?.style}>
				{this.props.header.map(
					(header, index) => {
						const thStylable = (typeof this.props.th === "function") ? this.props.th(header) : this.props.th;
						return <th key={`${this.props.id}-${index}`} className={thStylable?.className}
						           style={thStylable?.style}>{renderers[header](header)}</th>;
					})}
				{this.props.actions?.map((action, index) => <th key={`${this.props.id}-${this.props.header.length + index}`}/>)}
			</tr>
		);
	}

	private renderTableBody() {
		let renderers: RowRenderer<T>;
		if (typeof this.props.cellRenderer === "object")
			renderers = this.props.cellRenderer;
		else
			renderers = this.props.header.reduce((toRet, headerProp) => {
				toRet[headerProp] = this.props.cellRenderer as CellRenderer<keyof T, T[keyof T]>;
				return toRet;
			}, {} as RowRenderer<T>);

		let actionsRenderers: ActionsRenderer<A> | undefined;
		if (typeof this.props.actionsRenderer === "object")
			actionsRenderers = this.props.actionsRenderer;
		else
			actionsRenderers = this.props.actions?.reduce((toRet, actionKey) => {
				toRet[actionKey] = this.props.actionsRenderer as ActionItemRenderer<keyof A>;
				return toRet;
			}, {} as ActionsRenderer<A>);


		return this.props.rows.map((row: T, rowIndex: number) => {
			const trStylable = (typeof this.props.tr === "function") ? this.props.tr(row, rowIndex) : this.props.tr;
			return <tr key={`${this.props.id}-${rowIndex}`} className={trStylable?.className} style={trStylable?.style}>
				{this.props.header.map((header: keyof T, columnIndex: number) => {
					const tdStylable = (typeof this.props.td === "function") ? this.props.td(header) : this.props.td;
					return <td key={`${this.props.id}-${columnIndex}`} className={tdStylable?.className} style={tdStylable?.style}>
						{renderers[header](row[header], rowIndex, this.props.header[columnIndex])}
					</td>;
				})}
				{this.props.actions?.map((actionKey: keyof A, index: number) => {
					const tdStylable = (typeof this.props.tdActions === "function") ? this.props.tdActions(actionKey) : this.props.tdActions;
					return <td key={`${this.props.id}-${this.props.header.length + index}`} className={tdStylable?.className}
					           style={tdStylable?.style}>
						{actionsRenderers?.[actionKey](rowIndex, actionKey)}
					</td>;
				})}
			</tr>;
		});
	}
}
